import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import toastr from "toastr"

import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

import { ConfirmationModal } from "../../components/confirmation-modal"
import { SpinnerPage } from "../../components/ui/spinner"

import { useNavigateWithParams } from "hooks/navigation/use-navigate-with-params"
import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { useDeleteOrderMutation } from "hooks/api/order/use-delete-order-mutation"
import { useOrderData } from "hooks/api/order/use-order-data"
import { useCreateCheckoutSessionMutation } from "hooks/api/order/use-create-checkout-session-mutation"
import { useLocalStorage } from "hooks/utils/use-local-storage"

import { getNavigationPath } from "../../utils/navigation"

// Define Config object for Stripe key based on environment
const getStripePublishableKey = (accountId?: string): string => {
  const GUIDEBOOK_PRODUCTION_CSA_ORGANIZATION_ID = process.env.REACT_APP_ENV === "production" ? "1966965" : "1768794"
  if (accountId === GUIDEBOOK_PRODUCTION_CSA_ORGANIZATION_ID) {
    return "pk_test_51S0o1eS3noZLquHVrd7qzHB56lA0DXXzXW9MLXGO9q3W19XxQvGcnOXl5PcLifKd3VIN3a3Ker6XEofLSuPGEs5P00G11miI3m"
  }
  
  if (process.env.REACT_APP_ENV === "production") {
    return "pk_live_1KL4WGobjPvxhWeqpFszsLqU0aRCxG2ocancapfsRJkLaIpeERETL5h6ZKsbVVUkg4mpbAN1c2Lw4bkHZ2VIT7eHL003cx3kJG0"
  }
  // For staging and other environments, use the test key
  return "pk_test_1KL4WGobjPvxhWeqpFszsLqU0aRCxG2ocrl1IFOTZHgOg04sI6jnIddDChXV5kMKLnlGFnlGFnFXVD9rRNnEBaVTLHqRM00EW2F3Fu9"
}


interface CheckoutProps {}

interface CheckoutContext {
  params: {
    orderId: string
  }
}

const Checkout = (props: CheckoutProps, context: CheckoutContext) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get("order_id")
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stripePromise, setStripePromise] = useState<any>(null)
  const [checkoutId, setCheckoutId] = useState<string | null>(null)
  const { uuid } = useParams()
  const { stripeConnectedAccountId, owner_id } = useRegistrationData()
  const { localStorageData, setLocalStorageData } = useLocalStorage()
  const navigate = useNavigate()
  const navigateWithParams = useNavigateWithParams()
  const isPreview = window.location.search.includes("isPreview=true")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isNavigatingAway, setIsNavigatingAway] = useState(false)
  const [isCheckoutCompleting, setIsCheckoutCompleting] = useState(false)
  const deviceId = localStorage.getItem("device_id") || ""

  const { refetch: refetchOrder } = useOrderData({
    orderUuid: orderId || "",
    deviceId,
    enabled: false,
  })

  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation()

  const navigateToSelectTickets = () => {
    setIsNavigatingAway(true)
    setLocalStorageData(null)
    navigate(`/${uuid}/select-tickets`)
  }

  const deleteOrder = useDeleteOrderMutation({
    deviceId,
    onOrderDeleted: navigateToSelectTickets,
    onError: navigateToSelectTickets,
  })

  // Redirect if checkout hasn't started
  useEffect(() => {
    if (!localStorageData?.checkoutStarted) {
      navigate(getNavigationPath(`/${uuid}/ticket-details`, isPreview))
    }
  }, [localStorageData, navigate, uuid, isPreview])

  // Handle navigation away from checkout page - same pattern as ticket-details
  useEffect(() => {
    window.history.pushState(null, "", window.location.href)

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Alternative approach: Check if we're potentially navigating to success
      // This checks for common success indicators in the current context
      const isLikelySuccessNavigation = () => {
        // Check if there are any success-related parameters in the URL
        const urlParams = new URLSearchParams(window.location.search)
        const hasSuccessParams =
          urlParams.has("order_id") || urlParams.has("session_id")

        // Check if the URL contains success-related paths
        const hasSuccessPath = window.location.pathname.includes("success")

        return hasSuccessParams || hasSuccessPath
      }

      if (
        !isNavigatingAway &&
        !isCheckoutCompleting &&
        !isLikelySuccessNavigation()
      ) {
        e.preventDefault()
        e.returnValue = ""
      }
    }

    const handlePopState = () => {
      if (!isNavigatingAway && !isCheckoutCompleting) {
        window.history.pushState(null, "", window.location.href)
        setShowConfirmModal(true)
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("popstate", handlePopState)
    }
  }, [isNavigatingAway, isCheckoutCompleting])

  // Listen for Stripe checkout completion
  useEffect(() => {
    const handleCheckoutComplete = () => {
      setIsCheckoutCompleting(true)
    }

    // Listen for Stripe's success events
    window.addEventListener("stripe-checkout-success", handleCheckoutComplete)

    // Also check for URL changes that indicate success navigation
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    const checkForSuccessNavigation = (url: string) => {
      if (url.includes("/success") || url.includes("order_id=")) {
        setIsCheckoutCompleting(true)
      }
    }

    window.history.pushState = function (...args) {
      const url = args[2]?.toString() || ""
      checkForSuccessNavigation(url)
      return originalPushState.apply(this, args)
    }

    window.history.replaceState = function (...args) {
      const url = args[2]?.toString() || ""
      checkForSuccessNavigation(url)
      return originalReplaceState.apply(this, args)
    }

    return () => {
      window.removeEventListener(
        "stripe-checkout-success",
        handleCheckoutComplete
      )
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])

  const handleBack = () => {
    if (localStorageData?.uuid) {
      deleteOrder.mutate(localStorageData.uuid)
    } else {
      // If no order exists, just navigate back directly
      navigateToSelectTickets()
    }
  }

  useEffect(() => {
    // Check if we have local storage data and if the answers have been submitted
    if (!localStorageData || !localStorageData.answersSubmitted) {
      // If no local storage data or answers haven't been submitted, redirect back to ticket details
      toastr.warning(
        t(
          "checkout.complete_ticket_info",
          "Please complete your ticket information first"
        )
      )
      navigate(getNavigationPath(`/${uuid}/ticket-details`, isPreview))
      return
    }

    const initializeCheckout = async () => {
      if (!orderId) {
        // Navigate back to select-tickets page instead of showing error
        toastr.warning(
          t(
            "checkout.missing_order_info",
            "Missing order information. Please select your tickets again."
          )
        )
        navigateWithParams(
          getNavigationPath(`/${uuid}/select-tickets`, isPreview)
        )
        return
      }

      if (!clientSecret) {
        try {
          // First validate the order exists and is accessible
          const { data: orderResult, error: fetchError } = await refetchOrder()

          if (fetchError || !orderResult) {
            // If order doesn't exist or there's an error, navigate back to select tickets
            console.warn(
              "Order not found or expired. Please select your tickets again."
            )
            navigateToSelectTickets()
            return
          }

            // Initialize Stripe with the connected account from the order
            const ownerId = owner_id?.toString()
            const accountId = stripeConnectedAccountId
            const stripePublishableKey = getStripePublishableKey(ownerId)
          // Initialize Stripe with the connected account
          const stripe = loadStripe(stripePublishableKey, {
            stripeAccount: accountId,
          })
          setStripePromise(stripe)

          // Create checkout session
          if (!clientSecret && orderId && uuid) {
            const secret = await createCheckoutSessionMutation.mutateAsync({
              orderId,
              regUuid: uuid,
              deviceId,
            })
            setClientSecret(secret.clientSecret)
            setCheckoutId(secret.id || Date.now().toString())
          }
        } catch (err: unknown) {
          setError(
            err instanceof Error ? err.message : "An unknown error occurred"
          )
        } finally {
          setIsLoading(false)
        }
      }
    }

    setIsLoading(true)
    initializeCheckout()
  }, [
    orderId,
    uuid,
    stripeConnectedAccountId,
    navigateWithParams,
    isPreview,
    localStorageData,
  ])

  if (isLoading || !stripePromise || !clientSecret || !checkoutId) {
    return <SpinnerPage />
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  return (
    <div id="checkout">
      {clientSecret && stripePromise && (
        <EmbeddedCheckoutProvider
          key={checkoutId}
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleBack}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  )
}

Checkout.displayName = "Checkout"
export { Checkout }
